// Ultimate-mechanic reducers (8 new Effect kinds). ADR 0010.
//
// Each kind that requires ongoing state has a lifecycle record kept in
// BattleState (counterStances / storeAndReleasePools / sharedAbsorbPools /
// phaseCyclingAuras / anchoredHealHooks / damageConversions). Hook order
// during damage application is fixed (see `applyDamageWithHooks`):
//
//   1. sharedAbsorbPool   (pre-apply, mutates damage downward)
//   2. damageStoreAndRelease (pre-apply, mutates damage downward + accumulates)
//   3. applyDamage         (pure mutator)
//   4. counterStance       (post-apply, fires reverse strike unless isCounter)
//   5. anchoredHealOnHit   (post-apply, heals attacker / allies)
//   6. damageConversion    (post-apply, heals owner from damage taken)
//
// reflectingProjectile / transformOnHit are immediate-resolve (no state).
// phaseCyclingAura keeps state for phase scheduling but applies its buff via
// per-tick maintenance (no damage hook).
import { applyDamage } from '../damage.js';
import { applyEffect } from '../effects.js';
const HEAL_SCALE = 0.3;
// ---------------------------------------------------------------------------
// Element resolution helper (mirrors element-resolver.ts but stays internal)
// ---------------------------------------------------------------------------
import { resolveDamageElement } from '../element-resolver.js';
export function applyDamageWithHooks(attacker, target, finalDamage, state, options = {}) {
    let damageToApply = finalDamage * (1 - clampDamageReduction(target.damageReduction ?? 0));
    // 1. sharedAbsorbPool: pool absorbs first, hp untouched until cap is hit.
    damageToApply = applySharedAbsorbPoolHook(target, damageToApply, state);
    // 2. damageStoreAndRelease: target's store pool reduces incoming damage and
    //    accumulates the (reduced) amount for later release.
    damageToApply = applyDamageStoreHook(target, damageToApply, state);
    // 3. applyDamage (pure mutator) — never changed by B-2.
    const result = applyDamage(target, damageToApply);
    // 4. counterStance: skip if this damage is itself a counter strike.
    if (!options.isCounter) {
        fireCounterStanceHook(attacker, target, state);
    }
    // 5. anchoredHealOnHit: if target is the anchor, owner's allies get heals.
    fireAnchoredHealHook(attacker, target, state);
    // 6. damageConversion: target carrying a conversion instance heals from
    //    `damageToApply × fraction` (raw damage that reached applyDamage,
    //    not the original finalDamage — pre-hook reductions matter).
    fireDamageConversionHook(target, damageToApply, state);
    return {
        absorbedByShield: result.absorbedByShield,
        absorbedByHp: result.absorbedByHp,
        killed: result.killed,
        damageApplied: damageToApply,
    };
}
function clampDamageReduction(value) {
    if (!Number.isFinite(value))
        return 0;
    return Math.min(0.9, Math.max(0, value));
}
// ---------------------------------------------------------------------------
// pre-apply hooks (mutate damage downward)
// ---------------------------------------------------------------------------
function distance(a, b) {
    const dx = a.x - b.x;
    const dy = a.y - b.y;
    return Math.sqrt(dx * dx + dy * dy);
}
function applySharedAbsorbPoolHook(target, damage, state) {
    if (state.sharedAbsorbPools.length === 0 || damage <= 0)
        return damage;
    let remaining = damage;
    const passthrough = [];
    for (const pool of state.sharedAbsorbPools) {
        if (pool.side !== target.side)
            continue;
        if (distance(pool.position, target.pos) > pool.radius)
            continue;
        const free = pool.totalCapacity - pool.consumed;
        if (free <= 0)
            continue;
        const before = remaining;
        const absorbed = Math.min(free, before);
        pool.consumed += absorbed;
        remaining = before - absorbed;
        if (pool.overflowToHealRatio > 0 && before > absorbed) {
            // pushed は「この pool が吸収しきれず後続へ渡した量」。
            passthrough.push({ pool, pushed: before - absorbed });
        }
        if (remaining <= 0)
            break;
    }
    // overflow heal の原資は「全 pool 適用後に entity に届く最終 overflow」(ADR 0010 §2.5)。
    let finalOverflow = remaining;
    for (const { pool, pushed } of passthrough) {
        if (finalOverflow <= 0)
            break;
        const share = Math.min(pushed, finalOverflow);
        finalOverflow -= share;
        const owner = state.entities.find((e) => e.id === pool.ownerId);
        if (owner === undefined || owner.hp <= 0)
            continue;
        const healAmount = share * pool.overflowToHealRatio;
        owner.hp = Math.min(owner.hpMax, owner.hp + healAmount);
        if (healAmount > 0) {
            state.events.push({
                kind: 'heal',
                tick: state.tick,
                sourceId: owner.id,
                targetId: owner.id,
                amount: healAmount,
            });
        }
    }
    return remaining;
}
function applyDamageStoreHook(target, damage, state) {
    if (state.storeAndReleasePools.length === 0 || damage <= 0)
        return damage;
    let result = damage;
    for (const pool of state.storeAndReleasePools) {
        if (pool.ownerId !== target.id)
            continue;
        const reduced = result * (1 - pool.incomingDamageReduction);
        pool.accumulated += reduced;
        result = reduced;
    }
    return result;
}
// ---------------------------------------------------------------------------
// post-apply hooks (fire after applyDamage)
// ---------------------------------------------------------------------------
function fireCounterStanceHook(attacker, target, state) {
    if (state.counterStances.length === 0)
        return;
    // FIFO: first matching stance for this target consumes one counter.
    const idx = state.counterStances.findIndex((s) => s.ownerId === target.id);
    if (idx < 0)
        return;
    const stance = state.counterStances[idx];
    // Drop the stance now (default 1 counter; no maxCounters per ADR 0010 §2.3).
    state.counterStances.splice(idx, 1);
    // Fire reverse damage. Use a fixed-amount damage (counterPower scales like a
    // skill power, but to keep counter mechanics independent of weapon stats we
    // just multiply target.stats.weaponBase × counterPower as a quick stand-in;
    // a true crit/variance pass would normally apply via calculateDamage, but
    // the counter is a follow-up hit and uses isCounter to avoid recursion.
    const resolvedElement = resolveDamageElement(stance.counterElement, target);
    const counterDamage = target.stats.weaponBase * stance.counterPower;
    // Apply directly (bypass calculateDamage); ADR 0010 §2.4 spirit: counter is
    // a fixed payback, not a stat-rolled hit.
    if (attacker.hp > 0) {
        const result = applyDamageWithHooks(target, attacker, counterDamage, state, {
            isCounter: true,
        });
        state.events.push({
            kind: 'damage',
            tick: state.tick,
            sourceId: target.id,
            targetId: attacker.id,
            element: resolvedElement,
            amount: result.damageApplied,
            crit: false,
        });
        if (result.killed) {
            state.events.push({ kind: 'death', tick: state.tick, targetId: attacker.id });
        }
        if (stance.counterDebuff !== undefined) {
            // Defer to applyEffect's applyDebuff arm so stack/duration semantics stay
            // consistent with the rest of the engine.
            applyEffect({
                kind: 'applyDebuff',
                debuffId: stance.counterDebuff.debuffId,
                ...(stance.counterDebuff.stacks !== undefined
                    ? { stacks: stance.counterDebuff.stacks }
                    : {}),
                ...(stance.counterDebuff.durationTicks !== undefined
                    ? { durationTicks: stance.counterDebuff.durationTicks }
                    : {}),
            }, target, attacker, state, 'ult.counter-stance');
        }
    }
}
function fireAnchoredHealHook(attacker, target, state) {
    if (state.anchoredHealHooks.length === 0)
        return;
    for (const hook of state.anchoredHealHooks) {
        if (hook.anchorEntityId !== target.id)
            continue;
        const owner = state.entities.find((e) => e.id === hook.ownerId);
        if (owner === undefined)
            continue;
        // attacker side check — only allies of the owner fire the heal hook.
        if (attacker.side !== owner.side)
            continue;
        if (hook.payoutShape === 'attacker') {
            const amount = owner.hpMax * hook.healPowerPerHit * HEAL_SCALE;
            const healed = Math.min(attacker.hpMax - attacker.hp, amount);
            attacker.hp += healed;
            if (healed > 0) {
                state.events.push({
                    kind: 'heal',
                    tick: state.tick,
                    sourceId: owner.id,
                    targetId: attacker.id,
                    amount: healed,
                });
            }
        }
        else {
            const amount = owner.hpMax * hook.healPowerPerHit * HEAL_SCALE;
            for (const ally of state.entities) {
                if (ally.side !== owner.side || ally.hp <= 0)
                    continue;
                const healed = Math.min(ally.hpMax - ally.hp, amount);
                ally.hp += healed;
                if (healed > 0) {
                    state.events.push({
                        kind: 'heal',
                        tick: state.tick,
                        sourceId: owner.id,
                        targetId: ally.id,
                        amount: healed,
                    });
                }
            }
        }
    }
}
function fireDamageConversionHook(target, damageDealt, state) {
    if (state.damageConversions.length === 0 || damageDealt <= 0)
        return;
    for (const conv of state.damageConversions) {
        if (conv.ownerId !== target.id)
            continue;
        const healAmount = damageDealt * conv.fraction;
        if (healAmount <= 0)
            continue;
        if (conv.payoutShape === 'self') {
            const owner = state.entities.find((e) => e.id === conv.ownerId);
            if (owner === undefined || owner.hp <= 0)
                continue;
            const healed = Math.min(owner.hpMax - owner.hp, healAmount);
            owner.hp += healed;
            if (healed > 0) {
                state.events.push({
                    kind: 'heal',
                    tick: state.tick,
                    sourceId: owner.id,
                    targetId: owner.id,
                    amount: healed,
                });
            }
        }
        else {
            const owner = state.entities.find((e) => e.id === conv.ownerId);
            if (owner === undefined)
                continue;
            for (const ally of state.entities) {
                if (ally.side !== owner.side || ally.hp <= 0)
                    continue;
                const healed = Math.min(ally.hpMax - ally.hp, healAmount);
                ally.hp += healed;
                if (healed > 0) {
                    state.events.push({
                        kind: 'heal',
                        tick: state.tick,
                        sourceId: owner.id,
                        targetId: ally.id,
                        amount: healed,
                    });
                }
            }
        }
    }
}
// ---------------------------------------------------------------------------
// per-tick maintenance
// ---------------------------------------------------------------------------
export function tickUltMechanics(state) {
    const tick = state.tick;
    // counterStances: drop expired
    if (state.counterStances.length > 0) {
        state.counterStances = state.counterStances.filter((s) => s.expiresAt > tick);
    }
    // storeAndReleasePools: fire release on expiry
    if (state.storeAndReleasePools.length > 0) {
        const remaining = [];
        for (const pool of state.storeAndReleasePools) {
            if (pool.expiresAt > tick) {
                remaining.push(pool);
                continue;
            }
            releaseStoreAndRelease(pool, state);
        }
        state.storeAndReleasePools = remaining;
    }
    // sharedAbsorbPools: drop expired or full
    if (state.sharedAbsorbPools.length > 0) {
        state.sharedAbsorbPools = state.sharedAbsorbPools.filter((p) => p.expiresAt > tick && p.consumed < p.totalCapacity);
    }
    // phaseCyclingAuras: cycle phases + apply buff on phase change
    if (state.phaseCyclingAuras.length > 0) {
        const remaining = [];
        for (const aura of state.phaseCyclingAuras) {
            if (aura.expiresAt <= tick)
                continue;
            const elapsed = tick - aura.startTick;
            const phaseIndex = Math.floor(elapsed / aura.phaseTicks) % aura.phases.length;
            if (phaseIndex !== aura.lastFiredPhase) {
                firePhaseBuff(aura, phaseIndex, state);
                aura.lastFiredPhase = phaseIndex;
            }
            remaining.push(aura);
        }
        state.phaseCyclingAuras = remaining;
    }
    // anchoredHealHooks: drop expired or anchor dead
    if (state.anchoredHealHooks.length > 0) {
        state.anchoredHealHooks = state.anchoredHealHooks.filter((h) => {
            if (h.expiresAt <= tick)
                return false;
            const anchor = state.entities.find((e) => e.id === h.anchorEntityId);
            if (anchor === undefined || anchor.hp <= 0)
                return false;
            return true;
        });
    }
    // damageConversions: drop expired
    if (state.damageConversions.length > 0) {
        state.damageConversions = state.damageConversions.filter((c) => c.expiresAt > tick);
    }
}
function releaseStoreAndRelease(pool, state) {
    const owner = state.entities.find((e) => e.id === pool.ownerId);
    if (owner === undefined)
        return;
    const releaseDamage = pool.accumulated * pool.releaseMultiplier;
    if (releaseDamage <= 0)
        return;
    const resolvedElement = resolveDamageElement(pool.releaseElement, owner);
    // Pick targets by shape. Fixed-amount damage: bypass calculateDamage.
    const targets = [];
    if (pool.releaseShape === 'allEnemies') {
        for (const e of state.entities) {
            if (e.side !== owner.side && e.hp > 0)
                targets.push(e);
        }
    }
    else {
        // 'circle': use releaseRadius (default to 8 if absent — but spec adds
        // releaseRadius? as optional, so treat undefined as a safe default to keep
        // the reducer self-contained). Spec authors should always pin it.
        const radius = pool.releaseRadius ?? 8;
        for (const e of state.entities) {
            if (e.side === owner.side || e.hp <= 0)
                continue;
            if (distance(e.pos, owner.pos) <= radius)
                targets.push(e);
        }
    }
    for (const target of targets) {
        const result = applyDamageWithHooks(owner, target, releaseDamage, state);
        state.events.push({
            kind: 'damage',
            tick: state.tick,
            sourceId: owner.id,
            targetId: target.id,
            element: resolvedElement,
            amount: result.damageApplied,
            crit: false,
        });
        if (result.killed) {
            state.events.push({ kind: 'death', tick: state.tick, targetId: target.id });
        }
    }
}
function firePhaseBuff(aura, phaseIndex, state) {
    const element = aura.phases[phaseIndex];
    if (element === undefined)
        return;
    const buffId = aura.buffByElement[element];
    if (buffId === undefined)
        return;
    const owner = state.entities.find((e) => e.id === aura.ownerId);
    if (owner === undefined)
        return;
    const expiresAt = state.tick + aura.phaseTicks;
    for (const ally of state.entities) {
        if (ally.side !== owner.side || ally.hp <= 0)
            continue;
        upsertBuff(ally, buffId, expiresAt);
        state.events.push({
            kind: 'statusApplied',
            tick: state.tick,
            sourceId: owner.id,
            targetId: ally.id,
            statusId: buffId,
            stacks: 1,
        });
    }
}
function upsertBuff(target, buffId, expiresAt) {
    const existing = target.buffs.find((b) => b.id === buffId);
    if (existing !== undefined) {
        existing.expiresAt = expiresAt;
        return;
    }
    const fresh = { id: buffId, stacks: 1, expiresAt };
    target.buffs.push(fresh);
}
// ---------------------------------------------------------------------------
// Immediate-resolve reducers (no state)
// ---------------------------------------------------------------------------
/**
 * `reflectingProjectile` resolver: pick `projectileCount` projectiles, each
 * bouncing up to `maxBounces` times between living allies (caster included).
 * Each bounce applies onBounceBuff to the landing ally. ADR 0010 §2.7.
 */
export function resolveReflectingProjectile(effect, caster, state, skillId) {
    const allies = state.entities.filter((e) => e.side === caster.side && e.hp > 0);
    if (allies.length === 0)
        return;
    for (let p = 0; p < effect.projectileCount; p++) {
        let lastTarget = null;
        for (let b = 0; b < effect.maxBounces; b++) {
            // Pick next ally (different from lastTarget if possible).
            const prev = lastTarget;
            const pool = prev !== null ? allies.filter((a) => a.id !== prev.id) : allies;
            if (pool.length === 0)
                break;
            const idx = Math.floor(state.rng.nextFloat() * pool.length);
            const nextAlly = pool[Math.min(idx, pool.length - 1)];
            applyEffect({
                kind: 'applyBuff',
                buffId: effect.onBounceBuff.buffId,
                ...(effect.onBounceBuff.stacks !== undefined
                    ? { stacks: effect.onBounceBuff.stacks }
                    : {}),
                ...(effect.onBounceBuff.durationTicks !== undefined
                    ? { durationTicks: effect.onBounceBuff.durationTicks }
                    : {}),
            }, caster, nextAlly, state, skillId);
            lastTarget = nextAlly;
        }
    }
}
/**
 * `transformOnHit` resolver: deal first-stage damage on target (enemy),
 * then deliver `afterTransform` effects to the lowest-hp ally. ADR 0010 §2.7.
 */
export function resolveTransformOnHit(effect, caster, target, state, skillId) {
    // Stage 1: damage on target.
    applyEffect({
        kind: 'damage',
        element: effect.onHit.element,
        power: effect.onHit.power,
    }, caster, target, state, skillId);
    // Stage 2: pick lowest-hp ally and apply afterTransform.
    const allies = state.entities.filter((e) => e.side === caster.side && e.hp > 0);
    if (allies.length === 0)
        return;
    let lowest = allies[0];
    for (let i = 1; i < allies.length; i++) {
        if (allies[i].hp / allies[i].hpMax < lowest.hp / lowest.hpMax)
            lowest = allies[i];
    }
    for (const after of effect.afterTransform) {
        applyEffect(after, caster, lowest, state, skillId);
    }
}
//# sourceMappingURL=ult-mechanics.js.map